import {Injectable} from '@angular/core';
import * as Sentry from '@sentry/angular';
import {Product} from '../models/product';
import {AppState} from "./app-state";

interface ProductStorageState {
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly _localStorage = localStorage;
  private static readonly PRODUCT_KEYS = [
    'analysis',
    'contact',
    'externalId',
    'vehicle',
    'identificationScenario',
    'postcode',
    'appointmentServiceForm',
    'appointmentContactForm',
    'timeSlot',
    'urlContext',
    'redirectionURL',
    'analysisTiming',
  ] as const satisfies readonly (keyof AppState)[];

  private static readonly GLOBAL_KEYS = [
    'beta',
  ] as const satisfies readonly (keyof AppState)[];

  private readonly _productKeys = StorageService.PRODUCT_KEYS;
  private readonly _globalKeys = StorageService.GLOBAL_KEYS;

  private load<T>(key: string): T | null {
    try {
      const raw = this._localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw) as T;
    } catch (error) {
      Sentry.captureException(new Error(`Unable to parse StorageService.${key}`), { extra: { error } });
      return null;
    }
  }

  getProductState(product: Product): ProductStorageState {
    const productState: ProductStorageState = {};

    for (const key of this._productKeys) {
      const storageKey = `${product}.${key}`;
      const value = this.load(storageKey);
      productState[key] = value !== null ? value : undefined;
    }

    for (const key of this._globalKeys) {
      const value = this.load(key);
      productState[key] = value !== null ? value : undefined;
    }

    return productState;
  }

  toAppState(product: Product): Partial<AppState> {
    const productState = this.getProductState(product);
    if (!productState) return {};

    return {
      ...productState,
      product
    };
  }

  saveProductState(product: Product, state: Partial<AppState>) {
    if (!product) return;
    try {
      for (const key of this._productKeys) {
        const value = state[key];
        if (value === undefined) continue;

        const storageKey = `${product}.${key}`;
        const serialized = JSON.stringify(value);
        const existing = this._localStorage.getItem(storageKey);

        if (existing !== serialized) {
          this._localStorage.setItem(storageKey, serialized);
          console.log(`[StorageService] save: ${storageKey}`);
        }
      }

      for (const key of this._globalKeys) {
        const value = state[key];
        if (value === undefined) continue;

        const serialized = JSON.stringify(value);
        const existing = this._localStorage.getItem(key);

        if (existing !== serialized) {
          this._localStorage.setItem(key, serialized);
          console.log(`[StorageService] save: ${key}`);
        }
      }
    } catch (error) {
      Sentry.captureException(
        new Error('Unable to persist AppState to storage'),
        { extra: { error, product, state } }
      );
    }
  }

  clear(product: Product, excludes?: (keyof AppState)[]): void {
    if (!product) return;
    try {
      this._productKeys
        .filter(key => !excludes?.includes(key))
        .forEach(key => {
          const storageKey = `${product}.${key}`;
          this._localStorage.removeItem(storageKey);
          console.log(`[StorageService] removed ${storageKey}`);
        });
    } catch (error) {
      Sentry.captureException(
        new Error('Unable to clear product data from storage'),
        { extra: { error, product, excludes } }
      );
    }
  }

  clearOnly(product: Product, keys: (keyof AppState)[]) {
    if (!product) return;
    try {
      this._productKeys
        .filter(key => keys.includes(key))
        .forEach(key => {
          const storageKey = `${product}.${key}`;
          this._localStorage.removeItem(storageKey);
          console.log(`[StorageService] removed ${storageKey}`);
        });
    } catch (error) {
      Sentry.captureException(
        new Error('Unable to clear product data from storage'),
        { extra: { error, product, keys } }
      );
    }
  }

}
